import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Webhook, Play, Edit2, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Clock } from 'lucide-react';

const LOG_STATUS_COLORS = { SENT: 'bg-green-500 text-white', FAILED: 'bg-red-500 text-white', PENDING: 'bg-amber-500 text-white' };

function WebhookForm({ webhook, events, onSave, onCancel }) {
  const [form, setForm] = useState(webhook ? { name: webhook.name, targetUrl: webhook.targetUrl, subscribedEvents: webhook.subscribedEvents || [], active: webhook.active } : { name: '', targetUrl: '', subscribedEvents: [], active: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleEvent = (ev) => {
    set('subscribedEvents', form.subscribedEvents.includes(ev) ? form.subscribedEvents.filter(e => e !== ev) : [...form.subscribedEvents, ev]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (webhook?.id) {
        await api.put(`/integrations/webhooks/${webhook.id}`, form);
        toast.success('Webhook updated');
      } else {
        const { data } = await api.post('/integrations/webhooks', form);
        toast.success(`Webhook created. Secret: ${data.data.secret?.substring(0, 12)}...`);
      }
      onSave();
    } catch (e) { toast.error(e.response?.data?.detail?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div><Label>Name</Label><Input data-testid="webhook-name-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="My Webhook" /></div>
      <div><Label>Target URL</Label><Input data-testid="webhook-url-input" value={form.targetUrl} onChange={e => set('targetUrl', e.target.value)} placeholder="https://example.com/webhook" /></div>
      <div>
        <Label className="mb-2 block">Subscribed Events</Label>
        <div className="grid grid-cols-2 gap-2">
          {events.map(ev => (
            <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.subscribedEvents.includes(ev)} onCheckedChange={() => toggleEvent(ev)} />{ev}
            </label>
          ))}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="webhook-save-btn" onClick={handleSave} disabled={saving || !form.name || !form.targetUrl || form.subscribedEvents.length === 0}>{saving ? 'Saving...' : webhook?.id ? 'Update' : 'Create'}</Button>
      </DialogFooter>
    </div>
  );
}

export default function WebhookManagementPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [logs, setLogs] = useState({ items: [], total: 0 });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('webhooks');
  const [showForm, setShowForm] = useState(false);
  const [editWebhook, setEditWebhook] = useState(null);
  const [testing, setTesting] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logFilter, setLogFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [whRes, evRes] = await Promise.all([
        api.get('/integrations/webhooks?pageSize=50'),
        api.get('/integrations/webhooks/events'),
      ]);
      setWebhooks(whRes.data.items || []);
      setEvents(evRes.data.data?.events || []);
    } catch { toast.error('Failed to load webhooks'); }
    finally { setLoading(false); }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(logPage), pageSize: '20' });
      if (logFilter !== 'all') params.append('status', logFilter);
      const { data } = await api.get(`/integrations/webhooks/logs?${params}`);
      setLogs({ items: data.items || [], total: data.total || 0 });
    } catch { toast.error('Failed to load logs'); }
  }, [logPage, logFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);

  const handleTest = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/integrations/webhooks/${id}/test`);
      const d = data.data;
      if (d.deliveryStatus === 'SENT') toast.success(`Test successful (HTTP ${d.responseCode})`);
      else toast.error(`Test failed: ${d.responseBody?.substring(0, 100)}`);
      if (tab === 'logs') loadLogs();
    } catch { toast.error('Test failed'); }
    finally { setTesting(''); }
  };

  return (
    <div className="space-y-6" data-testid="webhook-management-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Webhook className="w-6 h-6" />Webhooks</h1>
        <Button onClick={() => { setEditWebhook(null); setShowForm(true); }} data-testid="create-webhook-btn"><Plus className="w-4 h-4 mr-2" />Add Webhook</Button>
      </div>

      <div className="flex gap-2">
        <Button variant={tab === 'webhooks' ? 'default' : 'outline'} size="sm" onClick={() => setTab('webhooks')} data-testid="tab-webhooks">Endpoints</Button>
        <Button variant={tab === 'logs' ? 'default' : 'outline'} size="sm" onClick={() => setTab('logs')} data-testid="tab-logs">Delivery Logs</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : tab === 'webhooks' ? (
        <div className="grid gap-3">
          {webhooks.map(w => (
            <Card key={w.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{w.name}</p>
                    <Badge variant={w.active ? 'default' : 'secondary'}>{w.active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 font-mono">{w.targetUrl}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">{w.subscribedEvents?.map(e => <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>)}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => handleTest(w.id)} disabled={testing === w.id} data-testid={`test-webhook-${w.id}`}><Play className="w-3 h-3 mr-1" />{testing === w.id ? '...' : 'Test'}</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditWebhook(w); setShowForm(true); }} data-testid={`edit-webhook-${w.id}`}><Edit2 className="w-3 h-3" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {webhooks.length === 0 && <p className="text-center text-muted-foreground py-8">No webhooks configured</p>}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-3">
            <Select value={logFilter} onValueChange={v => { setLogFilter(v); setLogPage(1); }}>
              <SelectTrigger className="w-36" data-testid="log-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="SENT">Sent</SelectItem><SelectItem value="FAILED">Failed</SelectItem><SelectItem value="PENDING">Pending</SelectItem></SelectContent>
            </Select>
          </div>
          {logs.items.map(l => (
            <Card key={l.id}>
              <CardContent className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  {l.status === 'SENT' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : l.status === 'FAILED' ? <XCircle className="w-4 h-4 text-red-500" /> : <Clock className="w-4 h-4 text-amber-500" />}
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={LOG_STATUS_COLORS[l.status]}>{l.status}</Badge>
                      {l.eventType && <span className="text-xs font-mono">{l.eventType}</span>}
                    </div>
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      {l.responseCode && <span>HTTP {l.responseCode}</span>}
                      <span>Attempts: {l.attemptCount}</span>
                      {l.lastAttemptAt && <span>{new Date(l.lastAttemptAt).toLocaleString()}</span>}
                    </div>
                    {l.status === 'FAILED' && l.responseBody && <p className="text-xs text-red-500 mt-1 truncate max-w-md">{l.responseBody}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {logs.items.length === 0 && <p className="text-center text-muted-foreground py-8">No delivery logs</p>}
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{logs.total} total</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={logPage <= 1} onClick={() => setLogPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm py-1">Page {logPage}</span>
              <Button size="sm" variant="outline" disabled={logs.items.length < 20} onClick={() => setLogPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editWebhook?.id ? 'Edit Webhook' : 'Add Webhook'}</DialogTitle></DialogHeader>
          <WebhookForm webhook={editWebhook} events={events} onSave={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
